import { useState, useEffect, useMemo } from "react";
import { getActions, useClient } from "ClientContext";
import { parse } from "date-fns";
import { IntegrationType } from "data/types";
import { set } from "lodash";

interface MessageEventData {
  data: {
    INTEGRATION_TYPE: IntegrationType;
    ACCESS_KEY: string;
    HERO_IMG: string;
    CLIENT_NAME: string;
    locationFilter: boolean;
    REQUEST_TYPE:
    | "ALL_LISTINGS"
    | "SINGLE_LISTING"
    | "PAYMENT_PAGE"
    | "SEARCH_LISTINGS";
    BUTTON_ICON_COLOR_ON_HOVER: string;
    PRIMARY_PALETTE: Array<string>;
    SECONDARY_PALETTE: Array<string>;
    NEUTRAL_PALETTE: Array<string>;
    HEADING_FONT: string;
    BODY_FONT: string;
    LISTING_ID: string;
    SEARCH_QUERY: searchQueryProps;
    FONT_LINK: string;
    MAX_GUESTS?: number;
    LODGIFY_WS_URL?: string;
    LODGIFY_WS_ID?: string;
  };
}

interface searchQueryProps {
  checkIn: Date;
  checkOut: Date;
  guestsCount: number;
  location?: string;
}

// Global buffer to capture early relevant messages
const globalMessageBuffer: Record<string, any>[] = [];

export const useIframeMessage = () => {
  const {
    setGlobal_language,
    setInternal_ID,
    setActions,
    setIntegration_TYPE,
    setClientName,
    setHeroImg,
    setIsLocationFilter,
    setRequestType,
    setGlobal_search,
    setGlobal_currency,
    setGlobal_max_guests,
    setLodgifySite,
    setWix_cms_api,
    setReferralId
  } = useClient();

  const [messageData, setMessageData] = useState<Record<string, any> | null>(
    null
  );
  const [listing_id, setListing_id] = useState<string | null>(null);
  const [, setSearchQuery] = useState<searchQueryProps | null>(null);
  const [timeoutError, setTimeoutError] = useState(false);
  const [actionsReady, setActionsReady] = useState(false);
  const [loading, setLoading] = useState(true); // Initial state is loading
  const [error] = useState<string | null>(null);

  useEffect(() => {
    const handleMessage = (event: MessageEventData) => {
      const { data } = event;
      if (!data?.INTEGRATION_TYPE || !data?.CLIENT_NAME) {
        //console.warn("Ignored irrelevant or incomplete message:", data);
        return;
      }

      setMessageData(data);
      setTimeoutError(false);
    };

    window.addEventListener("message", handleMessage);

    if (globalMessageBuffer.length > 0) {
      console.log("Processing early messages from buffer...");
      const bufferedMessage = globalMessageBuffer.shift();
      if (bufferedMessage) {
        setMessageData(bufferedMessage);
      }
    }

    const timeout = setTimeout(() => {
      if (!messageData) {
        console.warn("No message data received within timeout.");
        setTimeoutError(true);
        setLoading(false); // Stop loading if timeout occurs
      }
    }, 5000); // 5 seconds

    return () => {
      window.removeEventListener("message", handleMessage);
      clearTimeout(timeout);
    };
  }, [messageData]);

  useEffect(() => {
    if (messageData) {
      if (messageData.MAX_GUESTS) {
        setGlobal_max_guests(messageData.MAX_GUESTS);
      }
      if (messageData.LANG) {
        setGlobal_language(messageData.LANG);
      }
      if (messageData.WIX_CMS_URL) {
        setWix_cms_api({ cmsRequest: true, cmsURL: messageData.WIX_CMS_URL });
      }
      if (messageData.REFERRAL_UUID) {
        setReferralId(messageData.REFERRAL_UUID);
      }

      setInternal_ID(messageData.ACCESS_KEY);
      setIntegration_TYPE(messageData.INTEGRATION_TYPE);
      setClientName(messageData.CLIENT_NAME);
      setGlobal_currency(messageData.CURRENCIES);
      setHeroImg(messageData.HERO_IMG);
      setIsLocationFilter(messageData.locationFilter);
      setLodgifySite({
        id: messageData.LODGIFY_WS_ID,
        url: messageData.LODGIFY_WS_URL,
      });
      setRequestType(messageData.REQUEST_TYPE);
      setListing_id(messageData.LISTING_ID || null);
      setSearchQuery(messageData.SEARCH_QUERY || null);
      if (messageData.SEARCH_QUERY) {
        const { checkIn, checkOut, guestsCount, location } = messageData.SEARCH_QUERY;
        const checkIn_parse = checkIn
          ? parse(checkIn, "yyyy-MM-dd", new Date())
          : null; // Parse as local time
        const checkOut_parse = checkOut
          ? parse(checkOut, "yyyy-MM-dd", new Date())
          : null; // Parse as local time
        setGlobal_search((prevState) => ({
          ...prevState,
          params: {
            checkIn: checkIn_parse,
            checkOut: checkOut_parse,
            guestsCount: guestsCount,
            location: location
            ,
          },
        }));
      }

      // Apply color theme styles
      const applyColorsToDOM = (colorArray: string[]) => {
        colorArray.forEach((colorString) => {
          const [property, value] = colorString.split(":");
          if (property && value) {
            document.documentElement.style.setProperty(
              property.trim(),
              value.trim()
            );
          }
        });
        document.documentElement.style.setProperty(
          "--c-neutral-10",
          messageData.BUTTON_ICON_COLOR_ON_HOVER
        );
      };

      applyColorsToDOM(messageData.PRIMARY_PALETTE);
      applyColorsToDOM(messageData.SECONDARY_PALETTE);
      applyColorsToDOM(messageData.NEUTRAL_PALETTE);

      // Apply fonts
      document.body.style.fontWeight = "400";
      document.body.style.fontStyle = "normal";

      const linkElement = document.createElement("link");
      linkElement.rel = "stylesheet";
      linkElement.href = messageData.FONT_LINK;
      document.head.appendChild(linkElement);

      document.documentElement.style.setProperty(
        "--font-display",
        messageData.HEADING_FONT
      );
      document.documentElement.style.setProperty(
        "--font-body",
        messageData.BODY_FONT
      );
    }
  }, [messageData, setInternal_ID]);

  const Actions = useMemo(() => {
    if (messageData?.INTEGRATION_TYPE) {
      return getActions(messageData.INTEGRATION_TYPE);
    }
    return null;
  }, [messageData?.INTEGRATION_TYPE]);

  useEffect(() => {
    if (Actions) {
      setActions(Actions);
      setActionsReady(true);
    }
    setLoading(false); // Only stop loading when all actions are set
  }, [Actions, setActions]);

  return {
    listing_id,
    messageData,
    timeoutError,
    actionsReady,
    loading,
    error,
  };
};



// Attach global listener for early messages
window.addEventListener("message", (event: MessageEvent) => {
  const { data } = event;

  if (!data?.INTEGRATION_TYPE || !data?.CLIENT_NAME) {
    // console.warn(
    //   "Global listener ignored irrelevant or incomplete message:",
    //   data
    // );
    return;
  }

  // console.log("Global listener captured relevant message:", data);
  globalMessageBuffer.push(data);
});
